<template>
  <v-app
    v-if="doctorVideoUrl"
    data-video-card
  >
    <v-sheet
      class="rounded-pill overflow-hidden cursor-pointer"
      data-qa="doctor_video_card_play_color"
    >
      <v-img
        v-ripple
        :alt="$t('Открыть видеовизитку', { ns: $i18next.language })"
        width="32"
        height="32"
        src="/static/_v1/pd/icons/ui-kit/video-card-play.svg"
        @click="handleOpenDialog"
      />
    </v-sheet>
    <v-dialog
      v-if="isMobile"
      v-model="isDialogVisible"
      data-qa="doctor_video_card_dialog"
      fullscreen
      divider
    >
      <div
        class="video-card-wrapper text"
        data-qa="doctor_video_card_dialog"
      >
        <div
          class="video-card-player-close ui-icon_reset bg-gray-0--text ui-icon-close-not-a-circle"
          @click="isDialogVisible = false"
        />
        <div
          class="video-card-switch-state"
          @click="videoSwitchState"
        />
        <div
          v-if="!isVideoPlaying"
          class="video-card-play"
        >
          <div class="ui-icon_reset primary--text ui-icon-player-play" />
        </div>
        <div
          v-if="videoTimeTotal && videoTimeCurrent"
          class="video-card-time-cover"
        >
          <div class="video-card-time">
            <div
              class="text-body-1 bg-gray-0--text"
            >
              {{ videoTimeCurrent }}
            </div>
            <span class="text-body-1 bg-gray-60--text px-1">/</span>
            <div
              class="text-body-1 bg-gray-60--text"
            >
              {{ videoTimeTotal }}
            </div>
          </div>
        </div>
        <video
          ref="video"
          playsinline
          class="video-card-player"
          :src="doctorVideoUrl"
          @ended="handleEnded"
          @timeupdate="handleTimeupdate"
          @loadedmetadata="handleLoadedmetadata"
        />
      </div>
    </v-dialog>
    <CoreDialog
      v-else
      v-model="isDialogVisible"
      data-qa="doctor_video_card_dialog"
      size="lg"
      :title="$t('Видеовизитка', { ns: $i18next.language })"
      divider
    >
      <template #prependBtn="{ btnProps }">
        <v-btn
          v-bind="btnProps"
          @click="isDialogVisible = false"
        >
          <span class="ui-icon-close-not-a-circle" />
        </v-btn>
      </template>
      <video
        ref="video"
        controls
        class="video-card-player"
        :src="doctorVideoUrl"
        @ended="handleEnded"
      />
    </CoreDialog>
  </v-app>
</template>

<script>
import CoreDialog from 'src/core/CoreDialog/CoreDialog.vue'
import { VIDEO_CARD_GOALS } from 'www/yandexGoals'
import ManageYandexGoals from 'www/vue_components/common/mixins/ManageYandexGoals'
import { writeQuantityViews } from 'www/vue_components/common/VideoCard/api'
import { dispatchCustomEventGlobally } from 'www/utils'

export default {
  name: 'VideoCard',
  components: {
    CoreDialog,
  },
  mixins: [
    ManageYandexGoals,
  ],
  props: {
    doctorVideoUrl: {
      type: String,
      required: true,
    },
    doctorVideoUuid: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    timeoutId: null,
    videoTimeTotal: 0,
    videoTimeCurrent: 0,
    isVideoPlaying: false,
    isMobile: window.MOBILE_VERSION,
    isDialogVisible: false,
    yaGoalsList: VIDEO_CARD_GOALS,
  }),
  watch: {
    isDialogVisible(value) {
      if (!value) {
        this.handleCloseDialog()
      }
    },
  },
  mounted() {
    window.addEventListener('click-video-preview-image', this.handleOpenDialog)
  },
  methods: {
    handleCloseDialog() {
      this.isDialogVisible = false
      this.isVideoPlaying = true

      this.videoSwitchState()

      this.$nextTick(() => {
        if (this.$refs.video) {
          const diff = this.$refs.video.duration - this.$refs.video.currentTime

          if (diff !== 0) { // значит видео НЕ досмотрено до конца
            this.sendYandexGoal('closeWithoutView')
          }

          this.$refs.video.currentTime = 0

          dispatchCustomEventGlobally('aside-sizes:update')
        }
      })

      // Костыль, который снижает z-index кнопке. Иначе она может перекрыть собой иные открытые окна.
      // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
      this.timeoutId = setTimeout(() => { // Задержка нужна для десктопной версии, чтобы поиск не мигал при закрытии модального окна с видео
        [...document.querySelectorAll('.video-card-vue')].forEach(node => { node.style.zIndex = '5' })
      }, 500)
    },
    async handleOpenDialog() {
      this.isDialogVisible = true

      if (this.timeoutId) { // Если пользователь быстро закроет и откроет окно - z-index будет некорректным из-за setTimeout выше. Поэтому исполнение сброса z-index необходимо обнулить
        clearTimeout(this.timeoutId)
      }

      // Костыль, который повышает z-index кнопке.
      // Модальное окно должно иметь высокий z-index когда открыто, иначе иные элементы на странице могут перекрыть видео
      [...document.querySelectorAll('.video-card-vue')].forEach(node => { node.style.zIndex = '100' })

      this.videoSwitchState()
      this.sendYandexGoal('play')

      try {
        if (!this.doctorVideoUuid) {
          return
        }

        await writeQuantityViews(this.doctorVideoUuid)
      } catch { /* Empty */ }
    },
    videoSwitchState() {
      this.$nextTick(() => {
        if (!this.$refs.video) {
          return
        }

        if (this.isVideoPlaying) {
          this.isVideoPlaying = false
          this.$refs.video.pause()

          return
        }

        this.isVideoPlaying = true
        this.$refs.video.play()
      })
    },
    handleEnded() {
      this.sendYandexGoal('finish')
    },
    handleTimeupdate() {
      this.videoTimeCurrent = this.convertSeconds(this.$refs.video.currentTime)
    },
    handleLoadedmetadata() {
      this.videoTimeTotal = this.convertSeconds(this.$refs.video.duration)
      this.videoTimeCurrent = this.convertSeconds(this.$refs.video.currentTime)
    },
    convertSeconds(sec) {
      const date = new Date(1970, 0, 0, 0, 0, Number(sec) || 0)
        .toLocaleTimeString('ru')
        .split(':')

      return `${Number(date[1])}:${date[2]}`
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~www/themes/doctors/common/mixins';

#app[data-video-card] {
  @include v-app-reset;
}

.height-inherit {
  height: inherit;
}

.video-card {
  &-wrapper {
    position: relative;
  }

  &-player {
    width: 100%;
    height: 100vh;

    @supports (height: 100dvh) {
      height: 100dvh;
    }
  }

  &-play {
    z-index: 1;
    width: 40px;
    height: 40px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: calc(50% - 20px);
    left: calc(50% - 20px);
    background-color: $ui-kit-bg-primary;
    border-radius: $border-radius-circle;
  }

  &-player-close {
    top: 16px;
    left: 8px;
    z-index: 5;
    position: absolute;
  }

  &-switch-state {
    width: 100%;
    left: 0;
    bottom: 0;
    z-index: 5;
    position: absolute;
    height: calc(100% - 50px);
  }

  &-time-cover {
    width: 100%;
    left: 0;
    bottom: 100px;
    position: absolute;
    display: flex;
    justify-content: center;
  }

  &-time {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: $border-radius-md;
    background: rgba($ui-kit-text, 0.8);
  }
}

[data-flavour='full'] {
  .video-card-player {
    height: 100%;
  }
}

@media screen and (min-height: 900px) {
  [data-flavour='full'] {
    .video-card-player {
      max-height: 743px;
    }
  }
}
</style>

<style lang="scss">
#app[data-video-card] .v-dialog--fullscreen {
  overflow-y: hidden;
}

#app[data-video-card] .v-dialog:not(.v-dialog--fullscreen) {
  max-height: initial;
  height: 85%;
}

@media screen and (min-height: 900px) {
  [data-flavour='full'] {
    #app[data-video-card] .v-dialog:not(.v-dialog--fullscreen) {
      height: auto;
    }
  }
}
</style>
